import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer, Box, Typography, IconButton, Chip, CircularProgress, Divider, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonIcon from '@mui/icons-material/Person';
import artifactVersionService from '../../services/artifactVersionService';

const changeTypeConfig = {
  initial: { label: 'Initial', color: '#475969', bgColor: '#f5f5f5' },
  llm_tool_use: { label: 'AI generated', color: '#7c3aed', bgColor: '#f3e8ff' },
  llm_streaming: { label: 'AI generated', color: '#7c3aed', bgColor: '#f3e8ff' },
  user_edit: { label: 'User edit', color: '#1976d2', bgColor: '#e3f2fd' },
  patch: { label: 'Patch', color: '#0d9488', bgColor: '#ccfbf1' },
  rollback: { label: 'Rollback', color: '#f59e0b', bgColor: '#fef3c7' },
  reparse: { label: 'Re-parsed', color: '#475969', bgColor: '#f0f0f0' },
};

const diffTypeConfig = {
  added: { label: 'Added', color: '#2e7d32', bgColor: '#e8f5e9', icon: <AddCircleOutlineIcon sx={{ fontSize: 16 }} /> },
  removed: { label: 'Removed', color: '#d32f2f', bgColor: '#ffebee', icon: <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} /> },
  modified: { label: 'Modified', color: '#1565c0', bgColor: '#e3f2fd', icon: <EditIcon sx={{ fontSize: 16 }} /> },
  unchanged: { label: 'Unchanged', color: '#72849a', bgColor: '#f5f5f5', icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

/**
 * VersionDiffPanel — Shows version history with what changed between versions.
 * Fetches from the artifact version API and displays:
 *   - Timeline of all versions
 *   - Section-level diff (added/removed/modified/unchanged) between adjacent versions
 */
const VersionDiffPanel = ({ open, onClose, conversationId }) => {
  const [versionHistory, setVersionHistory] = useState(null);
  const [diffs, setDiffs] = useState({}); // { "v2": { added: [], removed: [], modified: [], unchanged: [] } }
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDiff, setLoadingDiff] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    try {
      const data = await artifactVersionService.getVersionHistory(conversationId, { limit: 50 });
      setVersionHistory(data);
    } catch {
      setVersionHistory(null);
    }
    setLoading(false);
  }, [conversationId]);

  useEffect(() => {
    if (open && conversationId) {
      fetchHistory();
      setExpandedVersion(null);
      setDiffs({});
    }
  }, [open, conversationId, fetchHistory]);

  const fetchDiff = useCallback(async (versionNumber) => {
    if (!conversationId || versionNumber <= 1) return;
    const key = `v${versionNumber}`;
    if (diffs[key]) {
      // Already loaded — just toggle
      setExpandedVersion(expandedVersion === versionNumber ? null : versionNumber);
      return;
    }
    setLoadingDiff(versionNumber);
    try {
      const data = await artifactVersionService.getDiff(conversationId, versionNumber - 1, versionNumber);
      setDiffs(prev => ({ ...prev, [key]: data.diff }));
      setExpandedVersion(versionNumber);
    } catch {
      // Silently fail
    }
    setLoadingDiff(null);
  }, [conversationId, diffs, expandedVersion]);

  const versions = versionHistory?.versions || [];
  const activeVersion = versionHistory?.active_version_number;

  // Helper to make section IDs human-readable
  const formatSectionId = (id) => {
    if (!id) return id;
    return id
      .replace(/^category-/, '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 520, bgcolor: '#fafafa',
          display: 'flex', flexDirection: 'column',
          top: '70px', height: 'calc(100% - 70px)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 2.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#fff', borderBottom: '1px solid #e5e5e5',
        flexShrink: 0
      }}>
        <Box>
          <Typography sx={{
            fontSize: 16, fontWeight: 700,
            fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
            color: '#475969'
          }}>
            Version history
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#72849a', mt: 0.25 }}>
            {versions.length} version{versions.length !== 1 ? 's' : ''} — click to see what changed
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#475969' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: '#475969' }} />
          </Box>
        ) : versions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e5e5' }}>
            <Typography sx={{ fontSize: 14, color: '#72849a' }}>No version history available</Typography>
            <Typography sx={{ fontSize: 12, color: '#a0aec0', mt: 0.5 }}>
              Version history appears after content is generated
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {versions.map((version, idx) => {
              const config = changeTypeConfig[version.change_type] || changeTypeConfig.initial;
              const isActive = version.version_number === activeVersion;
              const isExpanded = expandedVersion === version.version_number;
              const diffKey = `v${version.version_number}`;
              const diff = diffs[diffKey];
              const canShowDiff = version.version_number > 1;
              const isLoadingThis = loadingDiff === version.version_number;

              return (
                <React.Fragment key={version.id || idx}>
                  {/* Version entry */}
                  <Box
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: 2,
                      border: isActive ? '1.5px solid #1976d2' : '1px solid #e5e5e5',
                      p: 2,
                      cursor: canShowDiff ? 'pointer' : 'default',
                      transition: 'box-shadow 0.15s',
                      '&:hover': canShowDiff ? { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' } : {},
                    }}
                    onClick={() => canShowDiff && fetchDiff(version.version_number)}
                  >
                    {/* Top row: version number + type + date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#475969' }}>
                        v{version.version_number}
                      </Typography>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          height: 20, fontSize: 11, fontWeight: 500,
                          bgcolor: config.bgColor, color: config.color, border: 'none'
                        }}
                      />
                      {isActive && (
                        <Chip label="Current" size="small" sx={{
                          height: 20, fontSize: 11, fontWeight: 500,
                          bgcolor: '#e3f2fd', color: '#1976d2', border: 'none'
                        }} />
                      )}
                      <Typography sx={{ fontSize: 11, color: '#a0aec0', ml: 'auto' }}>
                        {formatDate(version.created_at)}
                      </Typography>
                    </Box>

                    {/* Change summary */}
                    {version.change_summary && (
                      <Typography sx={{ fontSize: 12, color: '#4a5568', mt: 0.75, lineHeight: 1.4 }}>
                        {version.change_summary}
                      </Typography>
                    )}

                    {/* Changed by */}
                    {version.changed_by && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 13, color: '#a0aec0' }} />
                        <Typography sx={{ fontSize: 11, color: '#a0aec0' }}>
                          Changed by user
                        </Typography>
                      </Box>
                    )}

                    {/* Changed sections quick summary */}
                    {version.changed_section_ids?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                        {version.changed_section_ids.slice(0, 5).map(sid => (
                          <Chip
                            key={sid}
                            label={formatSectionId(sid)}
                            size="small"
                            sx={{
                              height: 18, fontSize: 10,
                              bgcolor: '#f0f4ff', color: '#1565c0', border: 'none'
                            }}
                          />
                        ))}
                        {version.changed_section_ids.length > 5 && (
                          <Typography sx={{ fontSize: 10, color: '#a0aec0', alignSelf: 'center' }}>
                            +{version.changed_section_ids.length - 5} more
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Expand hint */}
                    {canShowDiff && !isExpanded && (
                      <Typography sx={{ fontSize: 11, color: '#1976d2', mt: 1, fontStyle: 'italic' }}>
                        {isLoadingThis ? 'Loading diff...' : 'Click to see what changed from previous version'}
                      </Typography>
                    )}

                    {/* Loading indicator */}
                    {isLoadingThis && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={18} sx={{ color: '#1976d2' }} />
                      </Box>
                    )}
                  </Box>

                  {/* Diff detail (expanded) */}
                  {isExpanded && diff && (
                    <Box sx={{
                      mx: 2, mb: 1, p: 2,
                      bgcolor: '#fff', border: '1px solid #e0e0e0', borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                    }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475969', mb: 1.5 }}>
                        Changes from v{version.version_number - 1} to v{version.version_number}
                      </Typography>

                      {/* Diff summary chips */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                        {diff.added?.length > 0 && (
                          <Chip icon={diffTypeConfig.added.icon} label={`${diff.added.length} added`} size="small"
                            sx={{ height: 22, fontSize: 11, bgcolor: diffTypeConfig.added.bgColor, color: diffTypeConfig.added.color, border: 'none', '& .MuiChip-icon': { color: diffTypeConfig.added.color } }} />
                        )}
                        {diff.removed?.length > 0 && (
                          <Chip icon={diffTypeConfig.removed.icon} label={`${diff.removed.length} removed`} size="small"
                            sx={{ height: 22, fontSize: 11, bgcolor: diffTypeConfig.removed.bgColor, color: diffTypeConfig.removed.color, border: 'none', '& .MuiChip-icon': { color: diffTypeConfig.removed.color } }} />
                        )}
                        {diff.modified?.length > 0 && (
                          <Chip icon={diffTypeConfig.modified.icon} label={`${diff.modified.length} modified`} size="small"
                            sx={{ height: 22, fontSize: 11, bgcolor: diffTypeConfig.modified.bgColor, color: diffTypeConfig.modified.color, border: 'none', '& .MuiChip-icon': { color: diffTypeConfig.modified.color } }} />
                        )}
                        {diff.unchanged?.length > 0 && (
                          <Tooltip title={`${diff.unchanged.length} section(s) unchanged`}>
                            <Chip label={`${diff.unchanged.length} unchanged`} size="small"
                              sx={{ height: 22, fontSize: 11, bgcolor: '#f5f5f5', color: '#72849a', border: 'none' }} />
                          </Tooltip>
                        )}
                      </Box>

                      {/* Individual changed sections with content diffs */}
                      {['added', 'removed', 'modified'].map(type => {
                        const items = diff[type] || [];
                        if (items.length === 0) return null;
                        const cfg = diffTypeConfig[type];
                        // Build lookup for modified_details
                        const detailMap = {};
                        (diff.modified_details || []).forEach(d => { detailMap[d.section_id] = d; });

                        return (
                          <Box key={type} sx={{ mb: 1.5 }}>
                            {items.map(sectionId => {
                              const detail = detailMap[sectionId];
                              return (
                                <Box key={sectionId} sx={{ mb: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex', alignItems: 'center', gap: 1,
                                      py: 0.5, px: 1,
                                      borderLeft: `3px solid ${cfg.color}`,
                                      bgcolor: cfg.bgColor,
                                      borderRadius: '0 4px 4px 0',
                                    }}
                                  >
                                    {cfg.icon}
                                    <Typography sx={{ fontSize: 12, color: cfg.color, fontWeight: 500, flex: 1 }}>
                                      {detail?.title || formatSectionId(sectionId)}
                                    </Typography>
                                    {detail && (
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {detail.added_lines > 0 && (
                                          <Typography sx={{ fontSize: 10, color: '#2e7d32', fontWeight: 600 }}>
                                            +{detail.added_lines}
                                          </Typography>
                                        )}
                                        {detail.removed_lines > 0 && (
                                          <Typography sx={{ fontSize: 10, color: '#d32f2f', fontWeight: 600 }}>
                                            −{detail.removed_lines}
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                  {/* Content diff for modified sections */}
                                  {detail?.diff && (
                                    <Box sx={{
                                      ml: 1.5, mt: 0.5, p: 1, maxHeight: 200, overflow: 'auto',
                                      bgcolor: '#1e1e1e', borderRadius: 1,
                                      fontFamily: '"SF Mono", "Fira Code", monospace',
                                      fontSize: 11, lineHeight: 1.5,
                                    }}>
                                      {detail.diff.split('\n').map((line, i) => {
                                        let color = '#d4d4d4';
                                        let bg = 'transparent';
                                        if (line.startsWith('+++') || line.startsWith('---')) {
                                          color = '#858585';
                                        } else if (line.startsWith('@@')) {
                                          color = '#569cd6'; bg = 'rgba(86,156,214,0.08)';
                                        } else if (line.startsWith('+')) {
                                          color = '#4ec9b0'; bg = 'rgba(78,201,176,0.1)';
                                        } else if (line.startsWith('-')) {
                                          color = '#f48771'; bg = 'rgba(244,135,113,0.1)';
                                        }
                                        return (
                                          <Box key={i} sx={{
                                            color, bgcolor: bg, px: 0.5,
                                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                          }}>
                                            {line || ' '}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      })}

                      {/* No changes */}
                      {(!diff.added?.length && !diff.removed?.length && !diff.modified?.length) && (
                        <Typography sx={{ fontSize: 12, color: '#72849a', fontStyle: 'italic' }}>
                          No structural changes — content may have been reformatted
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Connector line between versions */}
                  {idx < versions.length - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                      <Divider orientation="vertical" sx={{ height: 16, borderColor: '#d0d0d0' }} />
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

VersionDiffPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  conversationId: PropTypes.string,
};

export default React.memo(VersionDiffPanel);
